import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, Camera, Leaf, Minus, Plus, ShieldCheck, ShoppingBag, Star, Truck, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard, { Stars } from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const TABS = ["Benefits", "Actives", "Ritual", "Reviews"];

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("Benefits");
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const [sticky, setSticky] = useState(false);
  const [form, setForm] = useState({ author: "", rating: 5, title: "", text: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setProduct(null);
    setImgIdx(0);
    setTab("Benefits");
    api.getProduct(slug).then((p) => {
      setProduct(p);
      setVariant(p.variants[0].label);
    }).catch(() => navigate("/shop"));
    api.getRelated(slug).then(setRelated).catch(() => {});
    api.getReviews(slug).then(setReviews).catch(() => {});
  }, [slug, navigate]);

  useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <Leaf size={28} className="animate-pulse" style={{ color: "var(--mn-gold)" }} />
      </div>
    );
  }

  const price = product.variants.find((v) => v.label === variant)?.price || product.price;

  const onZoomMove = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    setZoom({ on: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5MB.");
      return;
    }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (form.author.trim().length < 2 || form.title.trim().length < 3 || form.text.trim().length < 10) {
      toast.error("Please complete all review fields (a few words each).");
      return;
    }
    setSubmitting(true);
    try {
      let photoPath = null;
      if (photo) {
        const up = await api.uploadReviewPhoto(photo);
        photoPath = up.path;
      }
      const created = await api.postReview(slug, { ...form, photo: photoPath });
      setReviews((r) => [created, ...r]);
      setForm({ author: "", rating: 5, title: "", text: "" });
      setPhoto(null);
      setPhotoPreview(null);
      toast.success("Thank you — your review is live.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-32 pt-28 sm:px-6 lg:px-8" data-testid="product-page">
      <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }}>
        <button onClick={() => navigate("/shop")} className="hover:text-[var(--mn-gold)]" data-testid="breadcrumb-shop">Shop</button>
        <span className="mx-2">/</span>
        <span style={{ color: "var(--mn-gold)" }}>{product.type}</span>
      </p>

      <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <div
            ref={imgRef}
            className="glass-card relative aspect-[4/5] cursor-zoom-in overflow-hidden rounded-3xl"
            onMouseMove={onZoomMove}
            onMouseLeave={() => setZoom((z) => ({ ...z, on: false }))}
            data-testid="product-gallery-main"
          >
            <img
              src={product.images[imgIdx]}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-200"
              style={zoom.on ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
            />
            {product.badge && (
              <span className="absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ background: product.badge === "New" ? "var(--mn-mint)" : product.badge === "Limited Edition" ? "var(--mn-terra)" : "var(--mn-gold)", color: "#070D09" }}>
                {product.badge}
              </span>
            )}
          </div>
          <div className="mt-4 flex gap-3">
            {product.images.map((src, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setImgIdx(i)}
                className="overflow-hidden rounded-2xl border-2 transition-all"
                style={{ borderColor: imgIdx === i ? "var(--mn-gold)" : "transparent" }}
                aria-label={`View image ${i + 1}`}
              >
                <img src={src} alt="" className="h-20 w-16 object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div>
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-gold)" }}>
            Mother Nature · Dermatologist Tested · AYUSH Certified
          </p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl" data-testid="product-name">{product.name}</h1>
          <p className="mt-2 font-serif text-base italic" style={{ color: "var(--mn-text2)" }}>{product.tagline}</p>
          <div className="mt-3 flex items-center gap-3">
            <Stars value={product.rating} size={15} />
            <span className="font-mono text-xs" style={{ color: "var(--mn-text2)" }}>
              {product.rating} · {product.reviews_count.toLocaleString("en-IN")} reviews
            </span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-mono text-3xl" style={{ color: "var(--mn-gold)" }} data-testid="product-price">{inr(price)}</span>
            {product.mrp > price && <span className="font-mono text-base line-through opacity-40">{inr(product.mrp)}</span>}
            {product.mrp > price && (
              <span className="rounded-full px-2.5 py-1 font-mono text-[0.6rem]" style={{ background: "var(--mn-surface)", color: "var(--mn-mint)" }}>
                Save {Math.round(((product.mrp - price) / product.mrp) * 100)}%
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-[0.6rem] uppercase tracking-widest" style={{ color: "var(--mn-text2)" }}>Inclusive of all taxes</p>

          <div className="mt-6">
            <p className="eyebrow mb-2.5">Size</p>
            <div className="flex flex-wrap gap-2">
              {product.variants.map((v) => (
                <button
                  key={v.label}
                  data-testid={`variant-${v.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setVariant(v.label)}
                  className={`rounded-full border px-5 py-2.5 font-mono text-sm transition-all ${variant === v.label ? "glow-gold" : ""}`}
                  style={{
                    borderColor: variant === v.label ? "var(--mn-gold)" : "var(--mn-glass-border)",
                    color: variant === v.label ? "var(--mn-gold)" : "var(--mn-text2)",
                  }}
                >
                  {v.label} · {inr(v.price)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7 flex items-center gap-3">
            <div className="flex items-center gap-3 rounded-full border px-4 py-3" style={{ borderColor: "var(--mn-glass-border)" }}>
              <button data-testid="pdp-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity"><Minus size={14} /></button>
              <span className="w-5 text-center font-mono" data-testid="pdp-qty">{qty}</span>
              <button data-testid="pdp-qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} aria-label="Increase quantity"><Plus size={14} /></button>
            </div>
            <button
              data-testid="pdp-add-to-cart"
              onClick={() => { addToCart(product, variant, qty); toast.success(`${product.name} added to your bag`); }}
              className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full py-3.5 text-sm"
            >
              <ShoppingBag size={16} /> Add to Bag
            </button>
          </div>
          <button
            data-testid="pdp-buy-now"
            onClick={() => { addToCart(product, variant, qty); navigate("/checkout"); }}
            className="btn-outline-glow mt-3 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm"
          >
            <Zap size={15} style={{ color: "var(--mn-mint)" }} /> Buy Now — UPI, Cards & COD
          </button>

          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[
              { icon: Truck, label: "Free ship ₹999+" },
              { icon: ShieldCheck, label: "Derm tested" },
              { icon: BadgeCheck, label: "Authentic batch" },
            ].map((b) => (
              <div key={b.label} className="glass-card rounded-2xl px-2 py-3">
                <b.icon size={16} className="mx-auto" style={{ color: "var(--mn-gold)" }} />
                <p className="mt-1.5 font-mono text-[0.58rem] uppercase tracking-wider" style={{ color: "var(--mn-text2)" }}>{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-16">
        <div className="flex gap-1 overflow-x-auto rounded-full border p-1" style={{ borderColor: "var(--mn-glass-border)" }} data-testid="pdp-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className="flex-1 whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-all"
              style={tab === t ? { background: "var(--mn-gold)", color: "#070D09", fontWeight: 600 } : { color: "var(--mn-text2)" }}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "Benefits" && (
            <div className="grid gap-8 lg:grid-cols-2" data-testid="tab-content-benefits">
              <p className="text-sm leading-relaxed sm:text-base" style={{ color: "var(--mn-text2)" }}>{product.description}</p>
              <ul className="space-y-3">
                {product.benefits.map((b, i) => (
                  <li key={i} className="glass-card flex items-start gap-3 rounded-2xl p-4 text-sm">
                    <BadgeCheck size={17} className="mt-0.5 shrink-0" style={{ color: "var(--mn-mint)" }} /> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tab === "Actives" && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tab-content-actives">
              {product.actives.map((a) => (
                <div key={a.name} className="glass-card rounded-2xl p-5">
                  <p className="font-mono text-2xl" style={{ color: "var(--mn-gold)" }}>{a.amount}</p>
                  <p className="mt-1.5 font-serif text-lg">{a.name}</p>
                  <p className="mt-1 font-mono text-[0.6rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-text2)" }}>
                    {product.collection === "science" ? "Science-backed active" : "Heritage botanical"}
                  </p>
                </div>
              ))}
            </div>
          )}
          {tab === "Ritual" && (
            <ol className="space-y-4" data-testid="tab-content-ritual">
              {product.how_to_use.map((step, i) => (
                <li key={i} className="glass-card flex items-center gap-5 rounded-2xl p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-serif text-lg" style={{ background: "var(--mn-surface)", color: "var(--mn-gold)" }}>
                    {i + 1}
                  </span>
                  <p className="text-sm">{step}</p>
                </li>
              ))}
            </ol>
          )}
          {tab === "Reviews" && (
            <div className="grid gap-10 lg:grid-cols-[1fr_380px]" data-testid="tab-content-reviews">
              <div className="space-y-4">
                {reviews.length === 0 && <p className="text-sm" style={{ color: "var(--mn-text2)" }}>Be the first to review this formula.</p>}
                {reviews.map((r) => (
                  <article key={r.id} className="glass-card rounded-2xl p-5" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <Stars value={r.rating} />
                      {r.verified && (
                        <span className="font-mono text-[0.58rem] uppercase tracking-widest" style={{ color: "var(--mn-mint)" }}>Verified buyer</span>
                      )}
                    </div>
                    <p className="mt-3 font-serif text-base">{r.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>{r.text}</p>
                    {r.photo && (
                      <img
                        src={fileUrl(r.photo)}
                        alt={`${r.author}'s skin progress`}
                        className="mt-3 h-28 w-28 rounded-xl object-cover"
                        style={{ border: "1px solid var(--mn-glass-border)" }}
                        loading="lazy"
                        data-testid={`review-photo-${r.id}`}
                      />
                    )}
                    <p className="mt-3 font-mono text-[0.62rem] uppercase tracking-widest" style={{ color: "var(--mn-text2)" }}>
                      {r.author} · {r.city}
                    </p>
                  </article>
                ))}
              </div>
              <form onSubmit={submitReview} className="glass-card h-fit rounded-3xl p-6" data-testid="review-form">
                <p className="font-serif text-lg">Share your ritual</p>
                <div className="mt-3 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setForm((f) => ({ ...f, rating: n }))} aria-label={`${n} stars`}>
                      <Star size={20} fill={n <= form.rating ? "var(--mn-gold)" : "none"} stroke="var(--mn-gold)" />
                    </button>
                  ))}
                </div>
                <input data-testid="review-name-input" value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} placeholder="Your name" className="mt-4 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />
                <input data-testid="review-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Review headline" className="mt-3 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />
                <textarea data-testid="review-text-input" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="How did your skin respond?" rows={4} className="mt-3 w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm outline-none focus:border-[var(--mn-gold)]" style={{ borderColor: "var(--mn-glass-border)" }} />

                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden" data-testid="review-photo-input" />
                {photoPreview ? (
                  <div className="relative mt-3 w-fit">
                    <img src={photoPreview} alt="Your upload preview" className="h-24 w-24 rounded-xl object-cover" style={{ border: "1px solid var(--mn-gold)" }} data-testid="review-photo-preview" />
                    <button
                      type="button"
                      data-testid="review-photo-remove"
                      onClick={() => { setPhoto(null); setPhotoPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                      className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full"
                      style={{ background: "var(--mn-terra)", color: "#070D09" }}
                      aria-label="Remove photo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="review-photo-button"
                    onClick={() => fileRef.current?.click()}
                    className="btn-outline-glow mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-dashed py-3 text-xs"
                    style={{ borderStyle: "dashed" }}
                  >
                    <Camera size={14} style={{ color: "var(--mn-mint)" }} /> Add a selfie or skin-progress photo
                  </button>
                )}

                <button data-testid="review-submit" type="submit" disabled={submitting} className="btn-gold mt-4 w-full rounded-full py-3 text-sm">
                  {submitting ? "Submitting…" : "Submit Review"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-24" data-testid="related-section">
          <Reveal>
            <p className="eyebrow">Complete the Ritual</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl">Pairs beautifully with</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} onQuickView={() => navigate(`/product/${p.slug}`)} />
            ))}
          </div>
        </div>
      )}

      {/* Sticky ATC */}
      <AnimatePresence>
        {sticky && (
          <motion.div
            data-testid="sticky-atc"
            className="fixed inset-x-0 bottom-0 z-[90] glass"
            style={{ background: "var(--mn-bg2)" }}
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
              <img src={product.images[0]} alt="" className="hidden h-12 w-10 rounded-lg object-cover sm:block" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif text-sm">{product.name}</p>
                <p className="font-mono text-xs" style={{ color: "var(--mn-gold)" }}>{inr(price)} · {variant}</p>
              </div>
              <button
                data-testid="sticky-atc-button"
                onClick={() => { addToCart(product, variant, qty); toast.success(`${product.name} added to your bag`); }}
                className="btn-gold flex items-center gap-2 rounded-full px-6 py-3 text-sm"
              >
                <ShoppingBag size={15} /> Add to Bag
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
